import {
    Box,
    Checkbox,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    FormControl,
    Select,
    SelectChangeEvent,
    ListSubheader,
    Divider,
} from '@mui/material'
import React, { useId } from 'react'
import { DropdownItem } from './DropdownItem'

type GroupedDropdownItems = Record<string, DropdownItem[]>

export interface DropdownSelectProps {
    disabled?: boolean
    label?: string
    items: readonly DropdownItem[] | GroupedDropdownItems
    isSlim?: boolean
    selectedItems: string[] | string
    onChange: (value: string[]) => void
    multiple?: boolean
    placeholder?: string
}

const getLabel = ({
    multiple,
    selected,
    items,
    placeholder,
}: {
    multiple?: boolean
    items: DropdownSelectProps['items']
    selected: string[] | string
    placeholder?: string
}) => {
    const itemArray: DropdownItem[] = Array.isArray(items) ? items : Object.values(items as GroupedDropdownItems).flat()

    if (!multiple) {
        const id = Array.isArray(selected) ? selected[0] : selected
        if (!id) return placeholder ?? ''

        return itemArray.find((item) => item.id === id)?.label ?? placeholder ?? ''
    }

    const currentSelected = Array.isArray(selected) ? selected : [selected]

    if (currentSelected.length === 0) {
        return placeholder ?? 'Visa samtliga'
    }

    if (currentSelected.length === itemArray.length) {
        return 'Visa samtliga'
    }

    if (currentSelected.length === 1) return '1 vald'

    return currentSelected.length + ' valda'
}

const DropdownSelect = ({
    disabled,
    label,
    items,
    isSlim,
    selectedItems,
    onChange,
    multiple,
    placeholder,
}: DropdownSelectProps) => {
    const labelId = useId()
    const handleChange = (event: SelectChangeEvent<string | string[]>) => {
        if (multiple) {
            onChange(event.target.value as string[])
        } else {
            onChange([event.target.value as string])
        }
    }

    const renderItems = (i: DropdownItem[]) =>
        i.map((item) => (
            <MenuItem key={item.id} dense sx={{ paddingTop: 0, paddingBottom: 0 }} value={item.id}>
                {multiple && <Checkbox checked={Array.isArray(selectedItems) && selectedItems.includes(item.id)} />}
                <ListItemText primary={item.label} />
            </MenuItem>
        ))

    return (
        <FormControl fullWidth={!isSlim}>
            {label && (
                <InputLabel id={labelId} shrink>
                    {label}
                </InputLabel>
            )}
            <Select
                fullWidth={!isSlim}
                labelId={labelId}
                disabled={disabled}
                displayEmpty
                multiple={multiple}
                value={selectedItems}
                onChange={handleChange}
                input={<OutlinedInput notched label={label} />}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getLabel({ multiple, selected, items, placeholder })}
                    </Box>
                )}
                size={isSlim ? 'small' : undefined}
                sx={{ paddingTop: 0 }}
            >
                {Array.isArray(items)
                    ? renderItems(items)
                    : Object.keys(items).map((header, index, arr) => {
                          const groupedItems = items as GroupedDropdownItems
                          return [
                              <ListSubheader key={header} sx={{ lineHeight: '32px' }}>
                                  {header}
                              </ListSubheader>,
                              renderItems(groupedItems[header]),
                              index < arr.length - 1 && <Divider sx={{ marginTop: 4 }} />,
                          ]
                      })}
            </Select>
        </FormControl>
    )
}

export default DropdownSelect
